import { Link } from 'react-router-dom';
import { ArrowRight, Cloud, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  POPULAR_TOOL_SLUGS,
  TOOLS,
  type ToolCategory,
  type ToolDef,
} from '../data';
import { iconFor } from '../icons';
import { getToolDetailHref, getToolSurfaceConfig, type ToolSurface } from './tool-surface-config';
import type { TFunction } from 'i18next';
import { useTranslation } from '@/i18n';
import { localizeTool } from '@/i18n/localized-tool';
import { Button } from '@/components/ui';

type Filter = 'all' | ToolCategory;
type ToolsGridVariant = 'public' | 'app';

interface ToolsGridProps {
  surface: ToolSurface;
  variant?: ToolsGridVariant;
  activeCategory?: Filter;
  searchQuery?: string;
  tools?: readonly ToolDef[];
  onResetFilters?: () => void;
  /** A filtered tool was opened; `position` is 1-based in the order the grid shows. */
  onToolOpen?: (slug: string, position: number) => void;
}

interface ToolCardProps {
  tool: ToolDef;
  surface: ToolSurface;
  variant: ToolsGridVariant;
  onOpen?: () => void;
}

const PUBLIC_CATEGORY_SUBHEADS: Partial<Record<ToolCategory, string>> = {
  accessibility: 'PDF/UA, Matterhorn, tagged-PDF signals, and human-review checks.',
};

const APP_CATEGORY_SUBHEADS: Record<ToolCategory, string> = {
  accessibility: 'PDF/UA, Matterhorn, tagged-PDF signals, and human-review checks.',
  organize: 'Merge, split, and reorder pages on-device.',
  edit: 'Redact, sign, watermark, and annotate without uploading.',
  'convert-to': 'Turn Word, Excel, and images into PDF.',
  'convert-from': 'Export PDFs to Markdown, Word, Excel, JSON, HTML, and more.',
  optimize: 'Compress, flatten, OCR, and inspect for sharing and storage.',
};

function filterTools(
  tools: readonly ToolDef[],
  activeCategory: Filter,
  searchQuery: string,
): ToolDef[] {
  const q = searchQuery.trim().toLowerCase();
  return tools.filter((tool) => {
    const matchesCategory = activeCategory === 'all' || tool.category === activeCategory;
    if (!matchesCategory) return false;
    if (!q) return true;
    return tool.name.toLowerCase().includes(q) || tool.description.toLowerCase().includes(q);
  });
}

/**
 * The tools the grid shows for a category and a query, in the order it shows
 * them (category sections in `CATEGORY_ORDER`). The index page uses it for the
 * result count and the position it reports in search analytics.
 */
export function visibleTools(
  tools: readonly ToolDef[],
  t: TFunction,
  activeCategory: Filter,
  searchQuery: string,
): ToolDef[] {
  const filtered = filterTools(
    tools.map((tool) => localizeTool(tool, t)),
    activeCategory,
    searchQuery,
  );
  const categories = activeCategory === 'all' ? CATEGORY_ORDER : [activeCategory];
  return categories.flatMap((category) => filtered.filter((tool) => tool.category === category));
}

export function ToolCard({ tool, surface, variant, onOpen }: ToolCardProps) {
  const { t } = useTranslation();
  const displayTool = localizeTool(tool, t);
  const config = getToolSurfaceConfig(surface);
  const Icon = iconFor(displayTool.iconName);
  const isCloud = displayTool.processingType === 'cloud';
  const isAccessibility = displayTool.category === 'accessibility';
  const href = getToolDetailHref(config, displayTool.slug);

  if (variant === 'app') {
    return (
      <Link
        to={href}
        onClick={onOpen}
        className="group flex min-h-[112px] min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4 text-left transition hover:-translate-y-px hover:border-primary/50 hover:shadow-card"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent text-accent-foreground">
            <Icon size={17} aria-hidden />
          </span>
          {isCloud ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-info-tint px-2 py-0.5 text-[10px] font-medium text-info-text">
              <Cloud className="h-3 w-3" aria-hidden />
              {t('tools.grid.cloudAi')}
            </span>
          ) : (
            <span
              title={t('tools.grid.browserTitle')}
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success-tint px-2 py-0.5 text-[10px] font-medium text-success-text"
            >
              <ShieldCheck className="h-3 w-3" aria-hidden />
              {t('tools.grid.onDevice')}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <span className="block truncate text-[13px] font-semibold text-foreground">
            {displayTool.name}
          </span>
          <span className="mt-1 line-clamp-2 block text-[12px] leading-5 text-muted-foreground">
            {displayTool.description}
          </span>
        </div>
        <span className="mt-auto inline-flex items-center gap-1 text-[11px] font-semibold text-primary transition-all group-hover:gap-1.5">
          {t('tools.grid.openTool')} <ArrowRight className="h-3 w-3" aria-hidden />
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      onClick={onOpen}
      className="group flex h-full min-w-0 flex-col rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-foreground">{displayTool.name}</h3>
        {isCloud ? (
          <span className="shrink-0 rounded-md bg-info-tint px-2 py-1 text-xs font-medium text-info-text">
            {t('tools.grid.cloud', { defaultValue: 'Cloud' })}
          </span>
        ) : isAccessibility ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-success-tint px-2 py-1 text-xs font-medium text-success-text">
            {t('tools.grid.compliance')}
          </span>
        ) : (
          <span
            title={t('tools.grid.browserTitle')}
            className="shrink-0 rounded-md bg-success-tint px-2 py-1 text-xs font-medium text-success-text"
          >
            {t('tools.grid.onDevice')}
          </span>
        )}
      </div>
      <p className="mb-5 flex-grow text-sm leading-6 text-muted-foreground">
        {displayTool.description}
      </p>
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary transition-all group-hover:gap-2">
        {t('tools.grid.useTool')} <ArrowRight className="h-4 w-4" />
      </span>
    </Link>
  );
}

export function ToolsGrid({
  surface,
  variant = surface,
  activeCategory = 'all',
  searchQuery = '',
  tools = TOOLS,
  onResetFilters,
  onToolOpen,
}: ToolsGridProps) {
  const { t } = useTranslation();
  const filteredTools = visibleTools(tools, t, activeCategory, searchQuery);
  const displayedCategories: ToolCategory[] =
    activeCategory === 'all' ? CATEGORY_ORDER : [activeCategory];
  const popularTools =
    variant === 'public' && activeCategory === 'all' && searchQuery.trim() === ''
      ? POPULAR_TOOL_SLUGS.flatMap((slug) => tools.filter((tool) => tool.slug === slug))
      : [];

  return (
    <div className={variant === 'public' ? 'space-y-10' : undefined}>
      {variant === 'public' && (activeCategory !== 'all' || searchQuery.trim()) ? (
        <div className="flex items-center justify-between gap-4 border-b border-border pb-3">
          <p role="status" className="text-sm text-muted-foreground">
            {t('tools.grid.results', {
              defaultValue: '{{count}} tools found',
              count: filteredTools.length,
            })}
          </p>
          {onResetFilters ? (
            <Button variant="outline" onClick={onResetFilters} className="min-h-11">
              {t('tools.grid.reset', { defaultValue: 'Clear filters' })}
            </Button>
          ) : null}
        </div>
      ) : null}
      {popularTools.length > 0 ? (
        <section id="popular" className="scroll-mt-24">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-foreground">
              {t('tools.grid.popular', { defaultValue: 'Popular tools' })}
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
              {t('tools.grid.popularSubhead', {
                defaultValue: 'The tools people reach for most.',
              })}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {popularTools.map((tool) => (
              <ToolCard
                key={`popular-${tool.slug}`}
                tool={tool}
                surface={surface}
                variant={variant}
              />
            ))}
          </div>
        </section>
      ) : null}
      {displayedCategories.map((catId) => {
        const categoryTools = filteredTools.filter((tool) => tool.category === catId);
        if (categoryTools.length === 0) return null;

        return (
          <section
            key={catId}
            id={variant === 'public' ? catId : undefined}
            className={variant === 'public' ? 'scroll-mt-24' : 'mt-8 first:mt-0'}
          >
            <CategoryHeading
              activeCategory={activeCategory}
              category={catId}
              count={categoryTools.length}
              variant={variant}
            />
            <div
              className={cn(
                'grid',
                variant === 'public'
                  ? 'grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'
                  : 'grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3',
              )}
            >
              {categoryTools.map((tool) => (
                <ToolCard
                  key={tool.slug}
                  tool={tool}
                  surface={surface}
                  variant={variant}
                  onOpen={
                    onToolOpen
                      ? () => onToolOpen(tool.slug, filteredTools.indexOf(tool) + 1)
                      : undefined
                  }
                />
              ))}
            </div>
          </section>
        );
      })}
      {filteredTools.length === 0 ? (
        <p
          role="status"
          className={cn(
            'text-center text-muted-foreground',
            variant === 'public' ? 'py-12' : 'py-8 text-sm',
          )}
        >
          {t('tools.grid.noMatch', { query: searchQuery })}
        </p>
      ) : null}
    </div>
  );
}

function CategoryHeading({
  activeCategory,
  category,
  count,
  variant,
}: {
  activeCategory: Filter;
  category: ToolCategory;
  count: number;
  variant: ToolsGridVariant;
}) {
  const { t } = useTranslation();
  const categoryTranslationKey = category.replace(/-([a-z])/g, (_, letter: string) =>
    letter.toUpperCase(),
  );
  if (variant === 'app') {
    return (
      <div className="mb-3 flex items-end justify-between gap-4 border-b border-border pb-2">
        <div className="min-w-0">
          <h2 className="text-[13px] font-semibold text-foreground">
            {t(`tools.categories.${categoryTranslationKey}`, {
              defaultValue: CATEGORY_LABELS[category],
            })}
          </h2>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            {t(`tools.grid.${categoryTranslationKey}Subhead`, {
              defaultValue: APP_CATEGORY_SUBHEADS[category],
            })}
          </p>
        </div>
        <span className="okra-mono shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('tools.grid.count', { count })}
        </span>
      </div>
    );
  }

  if (activeCategory !== 'all') return null;
  const subhead = PUBLIC_CATEGORY_SUBHEADS[category];

  return (
    <div className="mb-6">
      <h2 className="text-2xl font-bold text-foreground">
        {t(`tools.categories.${categoryTranslationKey}`, {
          defaultValue: CATEGORY_LABELS[category],
        })}
      </h2>
      {subhead ? (
        <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
          {t(`tools.grid.${categoryTranslationKey}Subhead`, { defaultValue: subhead })}
        </p>
      ) : null}
    </div>
  );
}
